import { 
  TimeBox, 
  CreateTimeBoxRequest, 
  UpdateTimeBoxRequest,
  TimeBoxStatus,
  TimeBoxCategory
} from '../../types/timeBox';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  getDoc,
  writeBatch,
  limit,
  onSnapshot,
  runTransaction,
  deleteField,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { logger } from '../../utils/logger';
import { taskService } from '../../features/task';
import { getCurrentUserId } from '../auth';
import { deleteTimeBoxLocal } from '../indexedDB';

// Firestore 集合名稱
const COLLECTION_NAME = 'timeBoxes';

// 即時監聽回調接口
interface TimeBoxSubscriptionCallbacks {
  onSnapshot: (timeBoxes: TimeBox[]) => void;
  onError: (error: Error) => void;
}

// 轉換 Firebase 時間戳為 Date 對象
const timestampToDate = (timestamp: Timestamp | Date | number | string): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  return new Date(timestamp);
};

// 將 Firebase 文件轉換為 TimeBox 對象
const convertDocToTimeBox = (doc: { id: string; data: () => Record<string, unknown> }): TimeBox => {
  const data = doc.data() as Record<string, unknown>;
  return {
    id: doc.id,
    itemType: 'timebox',
    userId: String(data.userId || ''),
    title: String(data.title || ''),
    description: data.description ? String(data.description) : undefined,
    startTime: timestampToDate(data.startTime as Timestamp | Date | number | string),
    endTime: timestampToDate(data.endTime as Timestamp | Date | number | string),
    category: String(data.category || '') as TimeBoxCategory,
    status: (data.status || 'normal') as TimeBoxStatus,
    taskId: data.taskId ? String(data.taskId) : undefined,
    taskIds: Array.isArray(data.taskIds) ? (data.taskIds as string[]) : undefined,
    location: data.location ? String(data.location) : undefined,
    // Fallback: 優先使用 notes，若無則使用 description（向後相容）
    notes: data.notes ? String(data.notes) : (data.description ? String(data.description) : undefined),
    createdAt: timestampToDate(data.createdAt as Timestamp | Date | number | string || new Date()),
    updatedAt: data.updatedAt ? timestampToDate(data.updatedAt as Timestamp | Date | number | string) : undefined,
  };
};

// 獲取指定日期的所有時間箱
export const getTimeBoxesByDate = async (date: Date, userId: string): Promise<TimeBox[]> => {
  try {
    // 設置查詢日期的起始和結束時間
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    // 查詢開始時間在指定日期範圍內的時間箱
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('startTime', '>=', startOfDay),
      where('startTime', '<=', endOfDay),
      orderBy('startTime', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const timeBoxes: TimeBox[] = [];

    querySnapshot.forEach((doc) => {
      timeBoxes.push(convertDocToTimeBox(doc));
    });

    return timeBoxes;
  } catch (error) {
    logger.error('Error getting timeBoxes by date:', error);
    throw error;
  }
};

// 根據日期範圍獲取時間箱 (支援週視圖和月視圖)
export const getTimeBoxesByDateRange = async (startDate: Date, endDate: Date, userId: string): Promise<TimeBox[]> => {
  try {
    // 確保日期範圍的正確格式
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    // 擴大查詢範圍：向前推7天以捕獲可能的跨日時間箱
    const extendedStart = new Date(start);
    extendedStart.setDate(extendedStart.getDate() - 7);

    // 查詢擴大範圍內的時間箱
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('startTime', '>=', extendedStart),
      where('startTime', '<=', end),
      orderBy('startTime', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const allTimeBoxes: TimeBox[] = [];

    querySnapshot.forEach((doc) => {
      allTimeBoxes.push(convertDocToTimeBox(doc));
    });

    // 客戶端過濾：保留與目標日期範圍有重疊的時間箱
    return allTimeBoxes.filter(timeBox => {
      const timeBoxStart = timeBox.startTime;
      const timeBoxEnd = timeBox.endTime;
      
      // 檢查時間箱是否與目標日期範圍有重疊
      return timeBoxStart <= end && timeBoxEnd >= start;
    });
  } catch (error) {
    logger.error('Error getting timeBoxes by date range:', error);
    throw error;
  }
};

// 獲取用戶的所有時間箱
export const getTimeBoxesByUser = async (
  userId: string, 
  options: { 
    limit?: number; 
    category?: TimeBoxCategory; 
    status?: TimeBoxStatus;
  } = {}
): Promise<TimeBox[]> => {
  try {
    let q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('startTime', 'desc')
    );

    // 添加類別過濾
    if (options.category) {
      q = query(q, where('category', '==', options.category));
    }

    // 添加狀態過濾
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }

    // 添加數量限制
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const querySnapshot = await getDocs(q);
    const timeBoxes: TimeBox[] = [];

    querySnapshot.forEach((doc) => {
      timeBoxes.push(convertDocToTimeBox(doc));
    });

    return timeBoxes;
  } catch (error) {
    logger.error('Error getting timeBoxes by user:', error);
    throw error;
  }
};

// 通過任務ID獲取關聯的時間箱
export const getTimeBoxesByTaskId = async (taskId: string, userId: string): Promise<TimeBox[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('taskId', '==', taskId),
      orderBy('startTime', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const timeBoxes: TimeBox[] = [];

    querySnapshot.forEach((doc) => {
      timeBoxes.push(convertDocToTimeBox(doc));
    });

    return timeBoxes;
  } catch (error) {
    logger.error('Error getting timeBoxes by taskId:', error);
    throw error;
  }
};

// 創建新的時間箱
export const createTimeBox = async (timeBoxData: CreateTimeBoxRequest): Promise<TimeBox> => {
  try {
    // 如果沒有提供 userId，使用當前用戶 ID
    const userId = timeBoxData.userId || getCurrentUserId();
    // 確保日期格式正確
    const startTime = timeBoxData.startTime instanceof Date 
      ? timeBoxData.startTime 
      : new Date(timeBoxData.startTime);
      
    const endTime = timeBoxData.endTime instanceof Date 
      ? timeBoxData.endTime 
      : new Date(timeBoxData.endTime);

    const currentTime = new Date();
    
    const timeBoxToSave = {
      itemType: 'timebox' as const,
      userId,
      title: timeBoxData.title,
      startTime,
      endTime,
      category: timeBoxData.category,
      status: timeBoxData.status || 'normal' as TimeBoxStatus,
      createdAt: currentTime,
      // 新 TimeBox 只使用 taskIds（不再填充 taskId）
      ...(timeBoxData.taskIds && { taskIds: timeBoxData.taskIds }),
      ...(timeBoxData.description && timeBoxData.description.trim() && { description: timeBoxData.description.trim() }),
      ...(timeBoxData.location && timeBoxData.location.trim() && { location: timeBoxData.location.trim() }),
      ...(timeBoxData.notes && timeBoxData.notes.trim() && { notes: timeBoxData.notes.trim() }),
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), timeBoxToSave);

    // 返回創建的時間箱
    const newTimeBox: TimeBox = {
      id: docRef.id,
      ...timeBoxToSave,
    };

    // 更新關聯任務的排程狀態（不再改為 CLOSED）
    const taskIdsToUpdate = timeBoxData.taskIds || (timeBoxData.taskId ? [timeBoxData.taskId] : []);

    for (const taskId of taskIdsToUpdate) {
      try {
        await taskService.addScheduledTimeBox(taskId, docRef.id);
        logger.info('TimeBox', '任務已標記為已排程', { taskId, timeBoxId: docRef.id });
      } catch (taskError) {
        logger.error('更新任務排程狀態失敗:', taskError);
        // 不拋出異常，因為時間箱已成功創建
      }
    }

    return newTimeBox;
  } catch (error) {
    logger.error('Error creating timeBox:', error);
    throw error;
  }
};

// 批量創建時間箱
export const batchCreateTimeBoxes = async (timeBoxDataList: CreateTimeBoxRequest[]): Promise<string[]> => {
  try {
    const batch = writeBatch(db);
    const newIds: string[] = [];
    const currentTime = new Date();
    const taskIds: string[] = []; // 存儲關聯的任務ID
    const userIds: string[] = []; // 存儲對應的用戶ID

    timeBoxDataList.forEach((timeBoxData) => {
      // 確保日期格式正確
      const startTime = timeBoxData.startTime instanceof Date 
        ? timeBoxData.startTime 
        : new Date(timeBoxData.startTime);
        
      const endTime = timeBoxData.endTime instanceof Date 
        ? timeBoxData.endTime 
        : new Date(timeBoxData.endTime);

        const timeBoxToSave = {
          userId: timeBoxData.userId || 'current-user-id', // 使用提供的userId或默認值
          title: timeBoxData.title,
          description: timeBoxData.description || '', // 明確處理 undefined 情況
          startTime,
          endTime,
          category: timeBoxData.category,
          status: timeBoxData.status || 'normal' as TimeBoxStatus,
          // 只在有值時添加以下欄位，避免傳遞 undefined 到 Firestore
          ...(timeBoxData.taskId ? { taskId: timeBoxData.taskId } : {}),
          ...(timeBoxData.location !== undefined ? { location: timeBoxData.location || '' } : {}),
          ...(timeBoxData.notes !== undefined ? { notes: timeBoxData.notes || '' } : {}),
          createdAt: currentTime,
        };

      const newDocRef = doc(collection(db, COLLECTION_NAME));
      newIds.push(newDocRef.id);
      batch.set(newDocRef, timeBoxToSave);
      
      // 如果有關聯任務，紀錄下來
      if (timeBoxData.taskId) {
        taskIds.push(timeBoxData.taskId);
        userIds.push(timeBoxToSave.userId);
      }
    });

    // 提交批量操作
    await batch.commit();
    
    // 處理關聯任務的狀態更新
    if (taskIds.length > 0) {
      for (let i = 0; i < taskIds.length; i++) {
        try {
          // 先將任務遷移計數加1
          await taskService.incrementMigrationCount(taskIds[i], userIds[i]);
          
          // 然後將任務狀態更新為 CLOSED，並設置關閉原因為 'scheduled'
          await taskService.updateTaskStatus(taskIds[i], 'CLOSED', userIds[i], 'scheduled');
        } catch (taskError) {
          logger.error(`更新任務 ${taskIds[i]} 狀態失敗:`, taskError);
          // 不中斷流程，繼續處理其他任務
        }
      }
    }
    
    return newIds;
  } catch (error) {
    logger.error('Error batch creating timeBoxes:', error);
    throw error;
  }
};

// 更新時間箱
export const updateTimeBox = async (timeBoxData: UpdateTimeBoxRequest): Promise<void> => {
  try {
    if (!timeBoxData.id) {
      throw new Error('Time box ID is required for update');
    }
    
    // 先獲取當前時間箱資料，檢查有無變更任務關聯
    const timeBoxRef = doc(db, COLLECTION_NAME, timeBoxData.id);
    const docSnap = await getDoc(timeBoxRef);
    
    if (!docSnap.exists()) {
      throw new Error(`Time box with ID ${timeBoxData.id} does not exist`);
    }
    
    const currentTimeBox = convertDocToTimeBox(docSnap);
    const oldTaskId = currentTimeBox.taskId;
    const newTaskId = timeBoxData.taskId;
    const userId = currentTimeBox.userId;
    
    // 準備更新資料
    const updateDataRaw: Record<string, unknown> = { ...timeBoxData };
    delete updateDataRaw.id; // 移除 id，因為它是文檔 ID 而非欄位

    // 確保日期格式正確
    if (updateDataRaw.startTime) {
      updateDataRaw.startTime = updateDataRaw.startTime instanceof Date
        ? updateDataRaw.startTime
        : new Date(updateDataRaw.startTime as string | number);
    }

    if (updateDataRaw.endTime) {
      updateDataRaw.endTime = updateDataRaw.endTime instanceof Date
        ? updateDataRaw.endTime
        : new Date(updateDataRaw.endTime as string | number);
    }

    // 添加更新時間
    updateDataRaw.updatedAt = new Date();

    // 處理 undefined 值：
    // - undefined 表示要刪除該欄位 → 使用 deleteField()
    // - 非 undefined 表示要更新該欄位 → 保留原值
    type FirestoreValue = Timestamp | Date | string | number | boolean | string[] | ReturnType<typeof deleteField>;
    const updateData: Record<string, FirestoreValue> = {};

    Object.keys(updateDataRaw).forEach((key) => {
      const value = updateDataRaw[key];
      if (value === undefined) {
        // 對於可選欄位（notes, location, description），undefined 表示要刪除
        if (['notes', 'location', 'description'].includes(key)) {
          updateData[key] = deleteField();
        }
        // 對於其他欄位，undefined 表示不更新（不包含在 updateData 中）
      } else {
        updateData[key] = value as Timestamp | Date | string | number | boolean | string[];
      }
    });

    // 更新時間箱
    await updateDoc(timeBoxRef, updateData);
    
    // 處理任務關聯變更
    // 注意：只有當 timeBoxData 明確包含 taskId 欄位時才處理任務關聯變更
    // 如果 taskId 是 undefined（沒有傳入），表示不更新任務關聯
    const hasTaskIdInUpdate = 'taskId' in timeBoxData;

    if (hasTaskIdInUpdate && oldTaskId !== newTaskId) {
      // 如果明確移除了原有任務關聯（newTaskId 為 null、undefined 或空字串）
      if (oldTaskId && (!newTaskId || newTaskId === '')) {
        try {
          // 將舊任務狀態恢復為 OPEN
          await taskService.updateTaskStatus(oldTaskId, 'OPEN', userId);
          logger.info(`任務 ${oldTaskId} 已恢復為 OPEN 狀態`);
        } catch (taskError) {
          logger.error(`恢復任務 ${oldTaskId} 狀態失敗:`, taskError);
        }
      }

      // 如果添加了新任務關聯（從無到有，或從一個任務切換到另一個任務）
      if (newTaskId && newTaskId !== '' && oldTaskId !== newTaskId) {
        try {
          // 如果是切換任務（有舊任務），先將舊任務恢復為 OPEN
          if (oldTaskId) {
            await taskService.updateTaskStatus(oldTaskId, 'OPEN', userId);
            logger.info(`舊任務 ${oldTaskId} 已恢復為 OPEN 狀態`);
          }

          // 將新任務遷移計數加1
          await taskService.incrementMigrationCount(newTaskId, userId);

          // 然後將新任務狀態更新為 CLOSED，並設置關閉原因為 'scheduled'
          await taskService.updateTaskStatus(newTaskId, 'CLOSED', userId, 'scheduled');
          logger.info(`任務 ${newTaskId} 已更新為 CLOSED 狀態，原因: scheduled`);
        } catch (taskError) {
          logger.error(`更新任務 ${newTaskId} 狀態失敗:`, taskError);
        }
      }
    }
  } catch (error) {
    logger.error('Error updating timeBox:', error);
    throw error;
  }
};

// 更新時間箱狀態
export const updateTimeBoxStatus = async (id: string, status: TimeBoxStatus): Promise<void> => {
  try {
    const timeBoxRef = doc(db, COLLECTION_NAME, id);
    
    // 檢查文檔是否存在
    const docSnap = await getDoc(timeBoxRef);
    if (!docSnap.exists()) {
      throw new Error(`Time box with ID ${id} does not exist`);
    }
    
    await updateDoc(timeBoxRef, { 
      status, 
      updatedAt: new Date() 
    });
    
    // 如果時間箱標記為已完成，且關聯了任務，則更新任務狀態
    if (status === 'cancelled') {  // 將 'completed' 改為 'cancelled'
      const timeBox = convertDocToTimeBox(docSnap);
      if (timeBox.taskId) {
        try {
          // 將任務狀態恢復為 OPEN（而不是 CLOSED）
          await taskService.updateTaskStatus(timeBox.taskId, 'OPEN', timeBox.userId);
          logger.info(`任務 ${timeBox.taskId} 已恢復為 OPEN 狀態 (時間箱已取消)`);
        } catch (taskError) {
          logger.error(`恢復任務 ${timeBox.taskId} 狀態失敗:`, taskError);
        }
      }
    }    
  } catch (error) {
    logger.error('Error updating timeBox status:', error);
    throw error;
  }
};

// 刪除時間箱
export const deleteTimeBox = async (id: string): Promise<void> => {
  try {
    const timeBoxRef = doc(db, COLLECTION_NAME, id);
    
    // 檢查文檔是否存在並獲取相關資訊
    const docSnap = await getDoc(timeBoxRef);
    if (!docSnap.exists()) {
      throw new Error(`Time box with ID ${id} does not exist`);
    }
    
    const timeBox = convertDocToTimeBox(docSnap);
    const taskId = timeBox.taskId;
    const userId = timeBox.userId;
    
    // 刪除時間箱
    await Promise.all([deleteDoc(timeBoxRef), deleteTimeBoxLocal(id)]);

    // 如果時間箱關聯了任務，恢復任務狀態為 OPEN
    if (taskId) {
      try {
        await taskService.updateTaskStatus(taskId, 'OPEN', userId);
        logger.info(`任務 ${taskId} 已恢復為 OPEN 狀態 (時間箱已刪除)`);
      } catch (taskError) {
        logger.error(`恢復任務 ${taskId} 狀態失敗:`, taskError);
      }
    }
  } catch (error) {
    logger.error('Error deleting timeBox:', error);
    throw error;
  }
};

// 批量刪除時間箱
export const batchDeleteTimeBoxes = async (ids: string[]): Promise<void> => {
  try {
    // 先獲取所有要刪除的時間箱資訊
    const timeBoxInfo: { id: string; taskId?: string; userId: string }[] = [];
    
    for (const id of ids) {
      const timeBoxRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(timeBoxRef);
      
      if (!docSnap.exists()) {
        logger.warn(`Time box with ID ${id} does not exist, skipping`);
        continue;
      }
      
      const timeBox = convertDocToTimeBox(docSnap);
      timeBoxInfo.push({
        id: id,
        taskId: timeBox.taskId,
        userId: timeBox.userId
      });
    }
    
    // 執行批量刪除
    const batch = writeBatch(db);
    timeBoxInfo.forEach(tb => {
      batch.delete(doc(db, COLLECTION_NAME, tb.id));
    });
    
    await batch.commit();
    await Promise.all(timeBoxInfo.map(tb => deleteTimeBoxLocal(tb.id)));

    // 處理關聯的任務
    for (const tb of timeBoxInfo) {
      if (tb.taskId) {
        try {
          // 恢復任務狀態為 OPEN
          await taskService.updateTaskStatus(tb.taskId, 'OPEN', tb.userId);
          logger.info(`任務 ${tb.taskId} 已恢復為 OPEN 狀態 (時間箱已刪除)`);
        } catch (taskError) {
          logger.error(`恢復任務 ${tb.taskId} 狀態失敗:`, taskError);
        }
      }
    }
  } catch (error) {
    logger.error('Error batch deleting timeBoxes:', error);
    throw error;
  }
};

// 獲取單個時間箱
export const getTimeBoxById = async (id: string): Promise<TimeBox | null> => {
  try {
    const timeBoxRef = doc(db, COLLECTION_NAME, id);
    const timeBoxSnapshot = await getDoc(timeBoxRef);
    
    if (timeBoxSnapshot.exists()) {
      return convertDocToTimeBox(timeBoxSnapshot);
    }
    
    return null;
  } catch (error) {
    logger.error('Error getting timeBox by id:', error);
    throw error;
  }
};

// 檢查時間衝突
export const checkTimeConflicts = async (
  startTime: Date, 
  endTime: Date, 
  userId: string,
  excludeTimeBoxId?: string
): Promise<TimeBox[]> => {
  try {
    // 查詢與指定時間範圍重疊的時間箱
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('startTime', '<', endTime),
      where('endTime', '>', startTime),
      orderBy('startTime', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const conflictingTimeBoxes: TimeBox[] = [];

    querySnapshot.forEach((doc) => {
      // 如果指定了排除ID，則跳過該時間箱
      if (excludeTimeBoxId && doc.id === excludeTimeBoxId) {
        return;
      }
      conflictingTimeBoxes.push(convertDocToTimeBox(doc));
    });

    return conflictingTimeBoxes;
  } catch (error) {
    logger.error('Error checking time conflicts:', error);
    throw error;
  }
};

/**
 * 獲取當前的時間箱
 * @param userId 用戶ID
 * @returns 當前時間箱或 null
 */
export const getCurrentTimeBox = async (userId: string): Promise<TimeBox | null> => {
  try {
    // 獲取當前時間
    const now = new Date();
    
    // 獲取今天所有的時間箱
    const todayTimeBoxes = await getTimeBoxesByDate(now, userId);
    
    // 查找當前時間箱（開始時間 <= 當前時間 <= 結束時間）
    const current = todayTimeBoxes.find(timeBox => {
      return (
        timeBox.startTime.getTime() <= now.getTime() && 
        timeBox.endTime.getTime() >= now.getTime()
      );
    }) || null;
    
    return current;
  } catch (error) {
    logger.error('獲取當前時間箱失敗:', error);
    return null;
  }
};

/**
 * 訂閱用戶的時間箱變更 (即時監聽)
 * @param userId 用戶ID
 * @param date 日期 (可選，默認為今天)
 * @param callbacks 回調函數
 * @returns 取消訂閱的函數
 */
export const subscribeToTimeBoxes = async (
  userId: string, 
  callbacks: TimeBoxSubscriptionCallbacks,
  date?: Date
): Promise<() => void> => {
  try {
    const targetDate = date || new Date();
    
    // 設置查詢日期的起始和結束時間
    const startOfDay = new Date(targetDate);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(targetDate);
    endOfDay.setHours(23, 59, 59, 999);

    // 創建查詢
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('startTime', '>=', startOfDay),
      where('startTime', '<=', endOfDay),
      orderBy('startTime', 'asc')
    );

    // 設置即時監聽
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        try {
          const timeBoxes: TimeBox[] = [];
          
          querySnapshot.forEach((doc) => {
            timeBoxes.push(convertDocToTimeBox(doc));
          });
          
          // 調用回調函數
          callbacks.onSnapshot(timeBoxes);
        } catch (error) {
          logger.error('處理時間箱快照失敗:', error);
          callbacks.onError(error instanceof Error ? error : new Error('處理快照失敗'));
        }
      },
      (error) => {
        logger.error('時間箱即時監聽失敗:', error);
        callbacks.onError(error);
      }
    );

    logger.dev('TimeBox', '即時監聽已啟動');
    return unsubscribe;
  } catch (error) {
    logger.error('啟動時間箱即時監聽失敗:', error);
    throw error;
  }
};

/**
 * 訂閱指定日期範圍內的時間箱變更 (即時監聽)
 * @param userId 用戶ID
 * @param startDate 開始日期
 * @param endDate 結束日期
 * @param callbacks 回調函數
 * @returns 取消訂閱的函數
 */
export const subscribeToTimeBoxesByDateRange = async (
  userId: string,
  startDate: Date,
  endDate: Date,
  callbacks: TimeBoxSubscriptionCallbacks
): Promise<() => void> => {
  try {
    // 確保日期範圍的正確格式
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    // 擴大查詢範圍：向前推7天以捕獲可能的跨日時間箱
    const extendedStart = new Date(start);
    extendedStart.setDate(extendedStart.getDate() - 7);

    // 查詢擴大範圍內的時間箱
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      where('startTime', '>=', extendedStart),
      where('startTime', '<=', end),
      orderBy('startTime', 'asc')
    );

    // 設置即時監聽
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        try {
          const allTimeBoxes: TimeBox[] = [];
          
          querySnapshot.forEach((doc) => {
            allTimeBoxes.push(convertDocToTimeBox(doc));
          });
          
          // 客戶端過濾：保留與目標日期範圍有重疊的時間箱
          const filteredTimeBoxes = allTimeBoxes.filter(timeBox => {
            const timeBoxStart = timeBox.startTime;
            const timeBoxEnd = timeBox.endTime;
            
            // 檢查時間箱是否與目標日期範圍有重疊
            return timeBoxStart <= end && timeBoxEnd >= start;
          });
          
          logger.info('📅 TimeBox subscription snapshot:', {
            dateRange: { start, end },
            totalTimeBoxes: allTimeBoxes.length,
            filteredTimeBoxes: filteredTimeBoxes.length,
            timeBoxes: filteredTimeBoxes.map(tb => ({ id: tb.id, title: tb.title, startTime: tb.startTime, endTime: tb.endTime }))
          });
          
          callbacks.onSnapshot(filteredTimeBoxes);
        } catch (error) {
          logger.error('Error processing TimeBox subscription snapshot:', error);
          callbacks.onError(error as Error);
        }
      },
      (error) => {
        logger.error('Error in TimeBox subscription:', error);
        callbacks.onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    logger.error('Error setting up TimeBox subscription:', error);
    callbacks.onError(error as Error);
    return () => {}; // 返回空的取消函數
  }
};

/**
 * 訂閱用戶的所有時間箱變更 (即時監聽) - 用於需要跨日期的場景
 * @param userId 用戶ID
 * @param callbacks 回調函數
 * @returns 取消訂閱的函數
 */
export const subscribeToAllTimeBoxes = async (
  userId: string, 
  callbacks: TimeBoxSubscriptionCallbacks
): Promise<() => void> => {
  try {
    // 創建查詢 - 獲取用戶的所有時間箱
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('startTime', 'desc')
    );

    // 設置即時監聽
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        try {
          const timeBoxes: TimeBox[] = [];
          
          querySnapshot.forEach((doc) => {
            timeBoxes.push(convertDocToTimeBox(doc));
          });
          
          // 調用回調函數
          callbacks.onSnapshot(timeBoxes);
        } catch (error) {
          logger.error('處理時間箱快照失敗:', error);
          callbacks.onError(error instanceof Error ? error : new Error('處理快照失敗'));
        }
      },
      (error) => {
        logger.error('時間箱即時監聽失敗:', error);
        callbacks.onError(error);
      }
    );

    logger.info('全部時間箱即時監聽已啟動');
    return unsubscribe;
  } catch (error) {
    logger.error('啟動全部時間箱即時監聽失敗:', error);
    throw error;
  }
};

/**
 * 將任務添加到現有 TimeBox
 */
export const addTaskToTimeBox = async (
  timeBoxId: string,
  taskId: string
): Promise<void> => {
  const timeBoxRef = doc(db, COLLECTION_NAME, timeBoxId);
  const taskRef = doc(db, 'tasks', taskId);

  // 使用 Transaction 確保一致性
  await runTransaction(db, async (transaction) => {
    // 🔴 重要：所有讀取必須在寫入之前完成
    // 1. 先讀取 TimeBox
    const timeBoxDoc = await transaction.get(timeBoxRef);

    // 2. 再讀取 Task
    const taskDoc = await transaction.get(taskRef);

    // 3. 檢查數據有效性
    if (!timeBoxDoc.exists()) {
      throw new Error(`TimeBox ${timeBoxId} not found`);
    }

    const timeBox = convertDocToTimeBox(timeBoxDoc);

    // 檢查任務是否已存在（向後兼容）
    const currentTaskIds = timeBox.taskIds || (timeBox.taskId ? [timeBox.taskId] : []);
    if (currentTaskIds.includes(taskId)) {
      logger.info('TimeBox', '任務已存在於 TimeBox，跳過', { timeBoxId, taskId });
      return;
    }

    // 4. 執行所有寫入操作
    // 更新 TimeBox
    const newTaskIds = [...currentTaskIds, taskId];
    transaction.update(timeBoxRef, {
      taskIds: newTaskIds,
      updatedAt: new Date(),
    });

    // 更新 Task
    if (taskDoc.exists()) {
      const task = taskDoc.data();
      const scheduledTimeBoxIds = task.scheduledTimeBoxIds || [];

      transaction.update(taskRef, {
        scheduledTimeBoxIds: [...scheduledTimeBoxIds, timeBoxId],
        updatedAt: new Date(),
      });
    }
  });

  logger.info('TimeBox', '任務已添加到 TimeBox', { timeBoxId, taskId });
};